/**
 * activity-log.js — writing and reading the activity log.
 *
 * TWO WAYS IN, ON PURPOSE
 *
 *   `stageActivity(tx, ...)` writes the entry INSIDE a transaction that is
 *   already running. Used by the voucher and account repositories, so the log
 *   entry and the thing it describes commit together: an invoice that saved
 *   with no log line, or a log line for an invoice that failed to save, are
 *   both worse than no log at all.
 *
 *   `recordActivity(...)` writes it on its own. Used for actions that are not
 *   transactional — a settings change, a role change made by a Cloud Function
 *   that has already returned. Its failure is swallowed deliberately: a
 *   business must not be unable to change somebody's role because the audit
 *   write was rejected. What that costs is a gap in the log, and the gap is
 *   the lesser harm; the console still gets the error.
 *
 * The log is append-only in the rules, like every ledger here. A log a user can
 * edit answers no question worth asking.
 */

import {
  addDoc,
  doc,
  limit as limitTo,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { activityCol } from './paths';
import { activityFields } from './audit';

export const SCHEMA_VERSION = 2;

/** The fields every entry carries, whichever way it was written. */
function entryDocument(fields, actor) {
  return {
    schemaVersion: SCHEMA_VERSION,
    ...activityFields({
      ...fields,
      actorUid: actor?.uid || fields.actorUid || null,
      actorName: actor?.name || fields.actorName || '',
      actorRole: actor?.role || fields.actorRole || '',
    }),
    at: serverTimestamp(),
  };
}

/**
 * Stage an entry on a transaction or batch that is already open.
 *
 * Takes the writer rather than opening its own, so the caller keeps control of
 * the atomicity. `doc(collection)` with no id generates the id client-side,
 * which is what makes this usable inside a transaction at all — `addDoc`
 * cannot be.
 */
export function stageActivity(writer, businessId, fields, actor = null) {
  if (!businessId || !fields?.type) return null;
  const ref = doc(activityCol(businessId));
  writer.set(ref, entryDocument(fields, actor));
  return ref;
}

/**
 * Write an entry on its own.
 *
 * Never throws. See the note at the top of the file: the action has already
 * happened by the time this is called, and failing the caller because the log
 * write was refused would turn a missing audit row into a broken feature.
 */
export async function recordActivity({ businessId, actor = null, ...fields }) {
  if (!businessId || !fields?.type) return null;
  try {
    const ref = await addDoc(activityCol(businessId), entryDocument(fields, actor));
    return { id: ref.id };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Could not write the activity log entry.', error);
    return null;
  }
}

/**
 * The activity feed, newest first.
 *
 * COMPOSITE INDEXES THIS NEEDS
 *   Activity: at DESC                     (single field, automatic)
 *   Activity: actorUid ASC, at DESC
 *   Activity: type ASC, at DESC
 */
export function activityQuery({ businessId, actorUid = null, type = null, max = null }) {
  const clauses = [];
  if (actorUid) clauses.push(where('actorUid', '==', actorUid));
  if (type) clauses.push(where('type', '==', type));
  const tail = max ? [limitTo(max)] : [];
  return query(activityCol(businessId), ...clauses, orderBy('at', 'desc'), ...tail);
}


